import type { ComponentProps, ReactNode } from 'react'
import styled from 'styled-components'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import Clickable from 'components/Common/Clickable'

const StyledClickable = styled(Clickable)`
  color: var(--button-internal-default-color);
  background-color: var(--button-internal-default-background-color);
  border: 1px solid var(--button-internal-default-border-color);
  transition: color 0.2s, background-color 0.2s, border-color 0.2s;
  box-sizing: border-box;

  // Hover styles
  ${mediaHoverable} {
    &:hover {
      color: var(--button-internal-hover-color);
      background-color: var(--button-internal-hover-background-color);
      border-color: var(--button-internal-hover-border-color);
    }
  }

  &[data-state='hover'] {
    color: var(--button-internal-hover-color);
    background-color: var(--button-internal-hover-background-color);
    border-color: var(--button-internal-hover-border-color);
  }

  // Active styles
  &:active, &[data-state='active'] {
    color: var(--button-internal-active-color);
    background-color: var(--button-internal-active-background-color);
    border-color: var(--button-internal-active-border-color);
  }

  // Focus styles
  &:focus-visible, &[data-state='focus'] {
    color: var(--button-internal-focus-color);
    background-color: var(--button-internal-focus-background-color);
    border-color: var(--button-internal-focus-border-color);

    outline: var(--button-internal-focus-ring-width, 2px) solid
      var(--button-internal-focus-ring-color, var(--palette-neutral-surface-light-foreground));
    outline-offset: 2px;
  }

  // Disabled styles
  &:disabled, &[aria-disabled='true'], &[data-state='disabled'] {
    color: var(--button-internal-disabled-color);
    background-color: var(--button-internal-disabled-background-color);
    border-color: var(--button-internal-disabled-border-color);

    opacity: var(--button-internal-disabled-opacity, 100%);
  }
`

interface Props extends ComponentProps<typeof Clickable>, Utils.PropsWithDataAttributes {
  children?: ReactNode
  nonInteractable?: boolean
}

function ButtonInternal({
  children,
  className,
  nonInteractable,
  ref,
  tabIndex,
  type = 'button',
  ...buttonCoreProps
}: Props) {
  // Use Clickable polymorphism unless we have specifically set the component to be nonInteractable
  const component = nonInteractable ? 'div' : undefined
  // If the component is a div, we don't want to set the type attribute
  const buttonType = component !== 'div' ? type : undefined

  return <StyledClickable
    {...buttonCoreProps}
    as={component}
    className={className}
    ref={ref}
    tabIndex={nonInteractable ? undefined : tabIndex}
    type={buttonType}
  >
    {children}
  </StyledClickable>
}

export default ButtonInternal
